import { format } from "date-fns";
import { useConversation } from "hooks/queries/use-conversations";
import { useCurrentAgent } from "hooks/queries/use-current-agent";
import { useMerchant } from "hooks/queries/use-settings-merchant";
import { useCustomFieldKeys } from "hooks/queries/use-group";
import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "redux/hooks";

export const getExampleValueForKey = (key: string) => {
  if (key.includes("date") && key.includes("time")) {
    return format(new Date(), "yyyy-MM-dd HH:MM:SS");
  }
  if (key.includes("date")) {
    return format(new Date(), "yyyy-MM-dd");
  }
  if (key.includes("year")) {
    return format(new Date(), "yyyy");
  }
  if (key.includes("month")) {
    return format(new Date(), "MM");
  }
  if (key.includes("day")) {
    return format(new Date(), "dd");
  }
  if (key.includes("time")) {
    return format(new Date(), "HH:MM:SS");
  }
  if (key.includes("first_name")) {
    return "John";
  }
  if (key.includes("last_name")) {
    return "Doe";
  }
  if (key.includes("name")) {
    return "John Doe";
  }
  if (key.includes("phone")) {
    return "+1234567890";
  }
  if (key.includes("email")) {
    return "john@doe.com";
  }
  if (key === "license_plate") {
    return "AB25 CDE";
  }
  return "";
};

export const populateCustomFieldsWithExampleValues = (
  customFields: Record<string, string>,
  templateCustomFields?: Record<string, string> | null
) => {
  const newCustomFields = { ...customFields };

  Object.keys(customFields).forEach((key) => {
    if (customFields[key] === "") {
      newCustomFields[key] = getExampleValueForKey(key);
    }
  });

  Object.keys(templateCustomFields || {}).forEach((key) => {
    if (templateCustomFields?.[key]) {
      newCustomFields[key] = templateCustomFields?.[key];
    }
  });

  return newCustomFields;
};

interface TemplateCustomFields {
  customFields: { [key: string]: string | undefined } | null;
}

export default function useAvailableCustomFields(
  template: TemplateCustomFields | null = null
) {
  const { data: currentAgent } = useCurrentAgent();
  const { activeConversationId } = useAppSelector(
    (state) => state.conversations
  );
  const { data: activeConversation } = useConversation(activeConversationId, {
    enabled: !!activeConversationId,
  });
  const { data: merchant } = useMerchant();
  const { data: customFieldKeys = {} } = useCustomFieldKeys();

  const baseCustomFields: Record<string, string> = useMemo(
    () => ({
      contact_first_name: activeConversation?.customerFirstName || "",
      contact_last_name: activeConversation?.customerLastName || "",
      agent_first_name: currentAgent?.name || "",
      agent_last_name: currentAgent?.surname || "",
      agent_name:
        currentAgent?.name && currentAgent?.surname
          ? `${currentAgent.name} ${currentAgent.surname}`
          : "",
      agent_phone: currentAgent?.phone || "",
      agent_email: currentAgent?.email || "",
      merchant_name: merchant.name || "",
      ...(template?.customFields || {}),
    }),
    [
      activeConversation?.customerFirstName,
      activeConversation?.customerLastName,
      currentAgent?.name,
      currentAgent?.surname,
      currentAgent?.phone,
      currentAgent?.email,
      merchant.name,
      template?.customFields,
    ]
  );

  const [allFields, setAllFields] =
    useState<Record<string, string>>(baseCustomFields);

  useEffect(() => {
    const extendedCustomFields = Object.values(customFieldKeys)
      .flat()
      .reduce(
        (fields, fieldKey) => {
          fields[fieldKey] = "";
          return fields;
        },
        {} as Record<string, string>
      );

    setAllFields({
      ...extendedCustomFields,
      ...baseCustomFields,
    });
  }, [customFieldKeys, baseCustomFields]);

  return allFields;
}

// export const customFieldRegExp = /{([a-z0-9]+(_[a-z0-9]*)*)+}/g; never do this because after exec it will retain last index
// Keep the pattern simple to avoid catastrophic backtracking on long strings,
// while still capturing the key for downstream consumers.
export const customFieldRegExp = "\\{([a-z0-9_]+)\\}";
